/**
 * Inline Authentication Form
 * Embeddable auth form for invite/claim pages.
 * Renders OAuth buttons + email/password form inline (no modal).
 */

import { useState } from 'react';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '@/contexts/auth-context';
import { validateEmail, validatePassword, validateDisplayName } from '@/utils/validationUtils';

interface InlineAuthFormProps {
	defaultMode?: 'login' | 'register';
	intendedUrl?: string;
}

type AuthMode = 'login' | 'register';

export function InlineAuthForm({
	defaultMode = 'register',
	intendedUrl,
}: InlineAuthFormProps) {
	const {
		authProviders,
		hasOAuth,
		requiresEmailAuth,
		login,
		loginWithEmail,
		register,
		error,
		clearError,
		setIntendedUrl,
	} = useAuth();

	const [mode, setMode] = useState<AuthMode>(defaultMode);
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [name, setName] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

	const showGitHub = authProviders?.github && hasOAuth;
	const showGoogle = authProviders?.google && hasOAuth;

	const resetForm = () => {
		setEmail('');
		setPassword('');
		setName('');
		setConfirmPassword('');
		setValidationErrors({});
		setShowPassword(false);
		clearError();
	};

	const switchMode = (newMode: AuthMode) => {
		setMode(newMode);
		resetForm();
	};

	const validateForm = (): boolean => {
		const errors: Record<string, string> = {};

		const emailResult = validateEmail(email);
		if (!emailResult.valid) {
			errors.email = emailResult.error || 'Invalid email';
		}

		if (!password) {
			errors.password = 'Password is required';
		} else if (mode === 'register') {
			const pwResult = validatePassword(password);
			if (!pwResult.valid && pwResult.errors?.length) {
				errors.password = pwResult.errors[0];
			}
		} else if (password.length < 8) {
			errors.password = 'Password must be at least 8 characters';
		}

		if (mode === 'register') {
			const nameResult = validateDisplayName(name);
			if (!nameResult.valid) {
				errors.name = nameResult.error || 'Name is required';
			}
			if (password !== confirmPassword) {
				errors.confirmPassword = 'Passwords do not match';
			}
		}

		setValidationErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validateForm()) return;

		if (intendedUrl) {
			setIntendedUrl(intendedUrl);
		}

		setIsLoading(true);
		try {
			if (mode === 'login') {
				await loginWithEmail({ email, password });
			} else {
				await register({ email, password, name: name.trim(), intendedUrl });
			}
		} catch {
			// Error is handled by auth context
		} finally {
			setIsLoading(false);
		}
	};

	const handleOAuthClick = (provider: 'google' | 'github') => {
		login(provider, intendedUrl);
	};

	return (
		<div className="space-y-4">
			{error && (
				<div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-2">
					<AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
					<p className="text-sm text-destructive">{error}</p>
				</div>
			)}

			{/* OAuth buttons */}
			{showGitHub && (
				<button
					onClick={() => handleOAuthClick('github')}
					className="w-full rounded-lg bg-gray-900 dark:bg-bg-1 p-3 text-white transition-all hover:bg-gray-800 dark:hover:bg-[#1a1e22] border border-gray-800 dark:border-bg-4"
				>
					<div className="flex items-center justify-center gap-2">
						<svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
							<path fillRule="evenodd" clipRule="evenodd" d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
						</svg>
						<span className="font-medium text-sm">Continue with GitHub</span>
					</div>
				</button>
			)}

			{showGoogle && (
				<button
					onClick={() => handleOAuthClick('google')}
					className="w-full rounded-lg bg-white dark:bg-bg-4 p-3 text-gray-800 dark:text-text-primary transition-all hover:bg-gray-50 dark:hover:bg-bg-4/80 border border-gray-200 dark:border-border-primary"
				>
					<div className="flex items-center justify-center gap-2">
						<svg className="h-5 w-5" viewBox="0 0 24 24">
							<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
							<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
							<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
							<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
						</svg>
						<span className="font-medium text-sm">Continue with Google</span>
					</div>
				</button>
			)}

			{/* Divider */}
			{requiresEmailAuth && hasOAuth && (
				<div className="relative">
					<div className="absolute inset-0 flex items-center">
						<div className="w-full border-t border-border" />
					</div>
					<div className="relative flex justify-center text-xs uppercase">
						<span className="bg-card px-2 text-muted-foreground">Or</span>
					</div>
				</div>
			)}

			{/* Email/Password Form */}
			{requiresEmailAuth && (
				<form onSubmit={handleSubmit} className="space-y-3">
					{mode === 'register' && (
						<div>
							<input
								type="text"
								placeholder="Full name"
								value={name}
								onChange={(e) => setName(e.target.value)}
								className={clsx(
									'w-full p-2.5 rounded-lg border bg-background text-sm transition-colors',
									validationErrors.name ? 'border-destructive' : 'border-border focus:border-primary',
								)}
								disabled={isLoading}
							/>
							{validationErrors.name && (
								<p className="mt-1 text-xs text-destructive">{validationErrors.name}</p>
							)}
						</div>
					)}

					<div>
						<input
							type="email"
							placeholder="Email address"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className={clsx(
								'w-full p-2.5 rounded-lg border bg-background text-sm transition-colors',
								validationErrors.email ? 'border-destructive' : 'border-border focus:border-primary',
							)}
							disabled={isLoading}
						/>
						{validationErrors.email && (
							<p className="mt-1 text-xs text-destructive">{validationErrors.email}</p>
						)}
					</div>

					<div className="relative">
						<input
							type={showPassword ? 'text' : 'password'}
							placeholder="Password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							className={clsx(
								'w-full p-2.5 pr-10 rounded-lg border bg-background text-sm transition-colors',
								validationErrors.password ? 'border-destructive' : 'border-border focus:border-primary',
							)}
							disabled={isLoading}
						/>
						<button
							type="button"
							onClick={() => setShowPassword(!showPassword)}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
							disabled={isLoading}
						>
							{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
						</button>
						{validationErrors.password && (
							<p className="mt-1 text-xs text-destructive">{validationErrors.password}</p>
						)}
					</div>

					{mode === 'register' && (
						<div>
							<input
								type="password"
								placeholder="Confirm password"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								className={clsx(
									'w-full p-2.5 rounded-lg border bg-background text-sm transition-colors',
									validationErrors.confirmPassword ? 'border-destructive' : 'border-border focus:border-primary',
								)}
								disabled={isLoading}
							/>
							{validationErrors.confirmPassword && (
								<p className="mt-1 text-xs text-destructive">{validationErrors.confirmPassword}</p>
							)}
						</div>
					)}

					<button
						type="submit"
						disabled={isLoading}
						className="w-full bg-primary hover:bg-primary/90 text-primary-foreground p-2.5 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{isLoading
							? (mode === 'register' ? 'Creating account...' : 'Signing in...')
							: (mode === 'register' ? 'Create account & join' : 'Sign in & join')
						}
					</button>
				</form>
			)}

			{/* Mode switching */}
			{requiresEmailAuth && (
				<div className="text-center">
					<button
						type="button"
						onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
						className="text-xs text-muted-foreground hover:text-foreground transition-colors"
					>
						{mode === 'login'
							? "Don't have an account? Sign up"
							: 'Already have an account? Sign in'
						}
					</button>
				</div>
			)}
		</div>
	);
}
